/**
 * **账户明细里的一行**（批 `151` 从 `AccountLedgerDrawer` 抽出）
 *
 * ## 🔴 为什么单独成组件
 *
 * 抽屉用的是 MUI `Drawer`，内容走 **Portal** ⇒ `react-dom/server` 静态渲染**拿不到**
 * （铁律 100 / 121）。抽出来之后这一行可以被静态渲染直接断言，
 * 不必为了测一个"余额显示没显示"去起真浏览器。
 *
 * ## 口径
 *
 * - 金额取腿的 `amount`（**恒正**）+ `direction`，服务端**不翻符号**，端侧按方向定正负；
 * - 分类名由服务端 JOIN 下发（批 `106-A`）—— 端侧**不按 `categoryId` 自己 mapping**（铁律 103）；
 * - 🆕 **余额列**（批 `151`）：`balanceAfter` 由服务端从当前余额倒序累减下发。
 *   `null` ⇒ **整块不渲染** —— 那是早于余额校准锚点的行，锚点已重置历史余额，
 *   硬算出来的数用户从没在账户里见过（`DR-056` D5）。显示 `0` 或推断值比不显示更糟。
 * - 展开子项**不改变任何合计**（`AC-B2.39`）：子项只回答"这笔钱是怎么分的"。
 */

import type { ReactElement } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import type { AccountLegRowView } from '@jiaying/shared';
import { toCents } from '@jiaying/shared';
import { Amount } from './Amount.js';

export interface LedgerRowItemProps {
  row: AccountLegRowView;
  /** 账户币种（多币种：余额不换算 —— 铁律 15） */
  currency: string;
  /** 该行是否展开（仅合并行有意义） */
  expanded: boolean;
  onToggle: () => void;
  /** 合并行展开后的子项是否渲染分隔线（列表最后一个不需要，交给调用方控制） */
  withDivider?: boolean;
}

export function LedgerRowItem({
  row,
  currency,
  expanded,
  onToggle,
  withDivider = true,
}: LedgerRowItemProps): ReactElement {
  const merged = row.legCount > 1;
  return (
    <>
      <Box
        data-testid={`ledger-row-${row.transactionId}-${row.direction}`}
        data-balance={row.balanceAfter === null ? 'none' : String(row.balanceAfter)}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {merged && row.categoryId === null
                ? `拆分 ${String(row.legCount)} 项`
                : // 🆕 批 153：无分类 ⇒ 按方向给文字（不再写「未分类」，也不回落显示分类 id）
                  (row.label ?? (row.direction === 'in' ? '收入' : '支出'))}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {row.date}
              {row.note !== null && row.note !== '' ? ` · ${row.note}` : ''}
              {merged ? ' · 已合并成一笔' : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <Stack alignItems="flex-end" spacing={0.25}>
              <Amount
                cents={toCents(row.direction === 'in' ? row.amount : -row.amount)}
                currency={currency}
                signed
                sx={{ fontSize: '0.875rem', fontWeight: 600, flexShrink: 0 }}
              />
              {/**
               * 🆕 该笔之后的余额（批 `151`）—— 对账就看这一列。
               * 🔴 `null` 一律不渲染（早于余额校准锚点，见文件头）。
               */}
              {row.balanceAfter !== null && (
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
                  data-testid={`ledger-bal-${row.transactionId}-${row.direction}`}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    余额
                  </Typography>
                  <Amount
                    cents={toCents(row.balanceAfter)}
                    currency={currency}
                    colored={false}
                    sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}
                  />
                </Box>
              )}
            </Stack>
            {merged && (
              <Button
                size="small"
                sx={{ minWidth: 0, px: 0.5 }}
                data-testid={`ledger-expand-${row.transactionId}`}
                onClick={onToggle}
              >
                {expanded ? '收起' : '展开'}
              </Button>
            )}
          </Stack>
        </Stack>
        {/* 🔴 展开**不改变任何合计**（`AC-B2.39`） */}
        {expanded && row.children !== undefined && (
          <Stack spacing={0.5} sx={{ mt: 1, pl: 1.5 }} data-testid="ledger-children">
            {row.children.map((child, idx) => (
              <Stack
                key={`${child.categoryId ?? 'none'}-${String(child.amount)}-${String(idx)}`}
                direction="row"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {child.label ?? (row.direction === 'in' ? '收入' : '支出')}
                </Typography>
                <Amount
                  cents={toCents(row.direction === 'in' ? child.amount : -child.amount)}
                  currency={currency}
                  signed
                  sx={{ fontSize: '0.75rem' }}
                />
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
      {withDivider && <Divider flexItem />}
    </>
  );
}
